/**
 * 前台会话状态。
 *
 * 认证走后台的统一认证中心（OIDC Authorization Code + PKCE）：
 * 前台是 public 客户端，不持有 client secret，令牌存储与续期由后端会话承担。
 * 本模块只负责「浏览器这一侧」的状态：
 *
 *   - 是否已登录（由 `GET /portal/me` 的探测结果决定，不自行解析令牌）
 *   - 登录入口跳转地址
 *   - 退出时清理本地状态并跳认证中心
 *
 * **不把令牌写入 localStorage**：public 客户端把 access token 暴露给 JS，
 * 会给 XSS 提供直接可用的凭据。会话 Cookie 由后端以 HttpOnly 方式下发。
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/core/api/client'

export interface PortalIdentity {
  userId: number
  username: string
  displayName: string | null
  /** 能力集合由服务端计算，前端不根据角色名推断 */
  capabilities: {
    favorite: boolean
    progress: boolean
    /** 旧版服务端未返回时默认隐藏管理入口。 */
    admin?: boolean
  }
}

export const useSessionStore = defineStore('portal-session', () => {
  const identity = ref<PortalIdentity | null>(null)
  const checked = ref(false)
  const checking = ref(false)

  const isAuthenticated = computed(() => identity.value !== null)

  /**
   * 探测当前会话。
   *
   * 未登录是正常状态：后端以 `data: null` 表达，不抛错。网络故障同样吞掉 ——
   * 前台大量页面匿名可读，一次探测失败不该让整页报错；`checked` 已置位，
   * 后续用户主动登录时仍会重新走完整流程。
   */
  async function refresh(): Promise<void> {
    if (checking.value) return
    checking.value = true
    try {
      identity.value = await api.get<PortalIdentity | null>('/portal/me')
    } catch {
      identity.value = null
    } finally {
      checked.value = true
      checking.value = false
    }
  }

  /** 跳转登录页，保留当前地址以便回跳。 */
  function gotoLogin(redirectTo?: string): void {
    const target = redirectTo ?? window.location.pathname + window.location.search
    window.location.href = `/login?redirect=${encodeURIComponent(target)}`
  }

  /**
   * 退出登录。
   *
   * 先清本地状态再导航：即使后端退出接口失败，浏览器侧的登录视图也必须立刻消失，
   * 否则用户会停在「看似已登录」的界面上。
   *
   * 后端返回 `logoutUrl` 时跳认证中心，这样 SSO 会话也一并结束；返回 null 说明
   * 未配置退出回调，留在前台即可 —— 直接跳认证中心会停在它的完成页，用户回不来。
   */
  async function logout(): Promise<void> {
    let logoutUrl: string | null = null
    try {
      const result = await api.post<{ logoutUrl: string | null } | null>('/portal/logout', {})
      logoutUrl = result?.logoutUrl ?? null
    } catch {
      // 退出接口不可用时仍要清掉本地状态，不能让用户卡在已登录视图
    } finally {
      identity.value = null
      checked.value = true
      checking.value = false
    }
    window.location.href = logoutUrl ?? '/'
  }

  return { identity, checked, checking, isAuthenticated, refresh, gotoLogin, logout }
})
